import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../context/AuthContext';
import AppLayout from '../../components/ui/AppLayout';
import HeaderBar from '../../components/ui/HeaderBar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import { recruiterAPI } from '../../utils/api';
import * as XLSX from 'xlsx';
import AddRecruiterModal from './components/AddRecruiterModal';
import EditRecruiterModal from './components/EditRecruiterModal';
import ViewRecruiterModal from './components/ViewRecruiterModal';
import Notifier from '../../components/ui/Notifier';
import PaginationComponent from '../../components/PaginationComponent';
import { useNotifier } from '../../context/NotificationContext';
import {
  BriefcaseIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const RecruiterManagement = () => {
  const notify = useNotifier();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  
  // Data States
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiters, setSelectedRecruiters] = useState([]);
  
  // Modal States
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    country: 'all'
  });

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const breadcrumbItems = [
    { label: 'Master Admin Dashboard', path: '/master-admin-dashboard' },
    { label: 'Recruiter Management', path: '/recruiter-management' }
  ];

  useEffect(() => {
    loadRecruiters();
  }, []);

  const loadRecruiters = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await recruiterAPI.getAllRecruiters();

      // Process recruiters data - handle different response formats
      let recruiters = [];
      if (result?.success && result?.data?.data) {
        recruiters = Array.isArray(result.data.data) ? result.data.data : [];
      } else if (result?.data) {
        recruiters = Array.isArray(result.data.data) ? result.data.data : [];
      } else if (Array.isArray(result)) {
        recruiters = result;
      }

      setRecruiters(recruiters);

    } catch (error) {
      console.error('Failed to load recruiters:', error);
      setError('Failed to load recruiters data');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search recruiters
  const filteredRecruiters = useMemo(() => {
    return recruiters.filter(recruiter => {
      const matchesSearch = recruiter.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           recruiter.representative_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           recruiter.fwcms_registered_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCountry = filters.country === 'all' || recruiter.country === filters.country;

      return matchesSearch && matchesCountry;
    });
  }, [recruiters, searchTerm, filters]);

  // Pagination logic for recruiters
  const totalRecruiters = filteredRecruiters.length;
  const totalPages = Math.ceil(totalRecruiters / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRecruiters = filteredRecruiters.slice(startIndex, endIndex);

  const paginationData = {
    currentPage,
    perPage: itemsPerPage,
    total: totalRecruiters,
    totalPages,
    from: totalRecruiters > 0 ? startIndex + 1 : 0,
    to: Math.min(endIndex, totalRecruiters)
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedRecruiters([]); // Clear selection when changing pages
  };

  const handlePerPageChange = (newPerPage) => {
    setItemsPerPage(newPerPage);
    setCurrentPage(1); // Reset to first page
    setSelectedRecruiters([]); // Clear selection
  };

  // Get unique countries for filter
  const countries = useMemo(() => {
    const uniqueCountries = [...new Set(recruiters.map(r => r.country).filter(Boolean))];
    return uniqueCountries.sort();
  }, [recruiters]);

  // Export to Excel
  const handleExportExcel = () => {
    try {
      const exportData = filteredRecruiters.map(recruiter => ({
        'Agency ID': recruiter.id,
        'Agency Name': recruiter.name,
        'Country': recruiter.country,
        'Representative Name': recruiter.representative_name,
        'Contact Number': recruiter.contact_no,
        'FWCMS Name': recruiter.fwcms_registered_name,
        'Address': recruiter.address,
        'Point of Entry': recruiter.point_of_entry,
        'Embassy': recruiter.embassy,
        'Registration Date': new Date(recruiter.created_at).toLocaleDateString()
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Recruiters');

      // Auto-size columns
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      worksheet['!cols'] = colWidths;

      const fileName = `recruiters_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      notify.error(`Exported ${exportData.length} recruiters to ${fileName}`);
    } catch (error) {
      console.error('Export failed:', error);
      setError('Export failed. Please try again.');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(date));
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800">
          <Icon name="CheckCircle" size={12} className="mr-1" />
          Active
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-error-100 text-error-800">
          <Icon name="XCircle" size={12} className="mr-1" />
          Inactive
        </span>
      );
    }
  };

  const isAllSelected = filteredRecruiters.length > 0 && selectedRecruiters.length === filteredRecruiters.length;
  const isIndeterminate = selectedRecruiters.length > 0 && selectedRecruiters.length < filteredRecruiters.length;

  const handleRecruiterSelect = (recruiterId, isSelected) => {
    if (isSelected) {
      setSelectedRecruiters(prev => [...prev, recruiterId]);
    } else {
      setSelectedRecruiters(prev => prev.filter(id => id !== recruiterId));
    }
  };

  const handleViewRecruiter = (recruiter) => {
    setSelectedRecruiter(recruiter);
    setShowViewModal(true);
  };

  const handleEditRecruiter = (recruiter) => {
    setSelectedRecruiter(recruiter);
    setShowEditModal(true);
  };

  const handleUpdateSuccess = (updatedRecruiter) => {
    setShowEditModal(false);
    loadRecruiters(); // Refresh data
    setNotification({ show: true, type: 'success', message: 'Recruiter updated!' });
  };

  // Modal handler functions
  const handleAddRecruiterSuccess = (newRecruiter) => {
    // Reload recruiters to get fresh data
    loadRecruiters();
    setNotification({ 
      show: true, 
      type: 'success', 
      message: 'Recruiter created successfully!' 
    });
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      // Select all recruiters on current page
      const currentPageRecruiterIds = currentRecruiters.map(recruiter => recruiter.id);
      setSelectedRecruiters(prev => [...new Set([...prev, ...currentPageRecruiterIds])]);
    } else {
      // Deselect all recruiters on current page
      const currentPageRecruiterIds = currentRecruiters.map(recruiter => recruiter.id);
      setSelectedRecruiters(prev => prev.filter(id => !currentPageRecruiterIds.includes(id)));
    }
  };

  return (
    <AppLayout sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
      <Helmet>
        <title>Recruiter Management - eBiodata CLAB</title>
      </Helmet>

      <div className="flex-1 flex flex-col min-h-screen bg-secondary-50">
        <HeaderBar 
          userRole={user?.role || 'admin'}
          userName={user?.name || user?.full_name || 'Master Admin'}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
        />
        
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <BreadcrumbNavigation items={breadcrumbItems} />
            
            {/* Header */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-text-primary">Recruiter Management</h1>
                  <p className="mt-1 text-sm text-gray-500">Manage recruiter agencies and their profiles.</p>
                </div>
                <div className="mt-4 sm:mt-0 flex space-x-3">
                  <button
                    onClick={handleExportExcel}
                    disabled={loading || filteredRecruiters.length === 0}
                    className="inline-flex items-center px-4 py-2 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    <Icon name="Download" size={16} className="mr-2" />
                    Export Excel
                  </button>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <Icon name="Building2" size={16} className="mr-2" />
                    Add Recruiter
                  </button>
                </div>
              </div>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={20} className="text-error mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-error-800">Error</h3>
                    <p className="text-sm text-error-700 mt-1">{error}</p>
                  </div>
                  <button
                    onClick={() => setError(null)}
                    className="ml-auto text-error-400 hover:text-error-600"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Stat cards */}
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-5">
                <div className="bg-white rounded-lg shadow p-5 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600">Total Recruiters</p>
                    <p className="text-3xl font-bold text-text-primary">{loading ? '...' : recruiters.length}</p>
                  </div>
                  <div className="bg-primary-100 rounded-full p-3">
                    <BriefcaseIcon className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-5 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600">Countries</p>
                    <p className="text-3xl font-bold text-text-primary">{loading ? '...' : countries.length}</p>
                  </div>
                  <div className="bg-success-100 rounded-full p-3">
                    <GlobeAltIcon className="h-6 w-6 text-success-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="search-recruiters" className="sr-only">Search Recruiters</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Search" size={16} className="text-secondary-500" />
                    </div>
                    <input
                      type="text"
                      id="search-recruiters"
                      placeholder="Search by agency name, representative, email"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="country-filter" className="sr-only">Filter by Country</label>
                  <select
                    id="country-filter"
                    value={filters.country}
                    onChange={(e) => setFilters(prev => ({ ...prev, country: e.target.value }))}
                    className="w-full py-2 px-3 border border-secondary-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Countries</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Recruiter Table */}
            <div className="mt-8 bg-white shadow-lg rounded-lg p-6">
              {loading && recruiters.length === 0 ? (
                <div className="p-12 text-center">
                  <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto mb-4" />
                  <p className="text-secondary-600">Loading recruiters...</p>
                </div>
              ) : totalRecruiters === 0 ? (
                <div className="p-12 text-center">
                  <Icon name="Building2" size={48} className="text-secondary-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">No Recruiters Found</h3>
                  <p className="text-secondary-600">No recruiters match your current filters.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-200">
                    <thead className="bg-secondary-50 border-b border-secondary-200">
                      <tr>
                        <th className="px-6 py-4 text-left">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={isAllSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = isIndeterminate;
                              }}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              className="w-4 h-4 text-primary border-secondary-300 rounded focus:ring-primary-500"
                              disabled={loading}
                            />
                          </div>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Agency
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Details
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-secondary-200">
                      {currentRecruiters.map((recruiter) => (
                        <tr key={recruiter.id} className="hover:bg-secondary-50 transition-colors duration-150">
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedRecruiters.includes(recruiter.id)}
                              onChange={(e) => handleRecruiterSelect(recruiter.id, e.target.checked)}
                              className="w-4 h-4 text-primary border-secondary-300 rounded focus:ring-primary-500"
                              disabled={loading}
                            />
                          </td>
                          <td className="px-6 py-4 align-top">
                            <div className="flex items-center">
                              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                                <Icon name="Building" size={16} className="text-accent-800" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-text-primary">{recruiter.name}</div>
                                <div className="text-sm text-secondary-600">ID: {recruiter.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap align-top">
                            <div className="text-sm text-secondary-900 font-medium">
                              {recruiter.representative_name}
                            </div>
                            <div className="text-sm text-secondary-600">
                              FWCMS: {recruiter.fwcms_registered_name || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap align-top">
                             <div className="text-sm text-secondary-900">{recruiter.contact_no}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap align-top">
                            <div className="text-sm text-secondary-900">{recruiter.country}</div>
                            <div className="text-sm text-secondary-600">
                              Embassy: {recruiter.embassy || 'N/A'}
                            </div>
                             <div className="text-sm text-secondary-600">
                              Entry: {recruiter.point_of_entry || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium align-top">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => handleViewRecruiter(recruiter)}
                                className="text-secondary-600 hover:text-secondary-900 p-1 rounded-md hover:bg-secondary-100"
                              >
                                <Icon name="Eye" size={16} />
                              </button>
                              <button 
                                onClick={() => handleEditRecruiter(recruiter)}
                                className="text-secondary-600 hover:text-secondary-900 p-1 rounded-md hover:bg-secondary-100"
                              >
                                <Icon name="Edit" size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalRecruiters > 0 && (
              <div className="mt-6">
                <PaginationComponent
                  pagination={paginationData}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  showPerPageSelector={true}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {showAddModal && (
        <AddRecruiterModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddRecruiterSuccess}
        />
      )}
      
      {showViewModal && (
        <ViewRecruiterModal
            isOpen={showViewModal}
            onClose={() => setShowViewModal(false)}
            recruiter={selectedRecruiter}
        />
      )}

      {showEditModal && (
        <EditRecruiterModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            recruiter={selectedRecruiter}
            onUpdated={handleUpdateSuccess}
        />
      )}

      {notification.show && (
        <Notifier
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification({ ...notification, show: false })}
        />
      )}
    </AppLayout>
  );
};

export default RecruiterManagement;